<!-- eslint-disable vue/no-v-html -->
<template>
  <Modal
    v-bind="$attrs"
    buttons-class=""
    hide-close-button
    :modal-style="dialogStyle"
    :offset-menu="offsetMenu"
    :persistent="persistent"
    @close="emit('cancel')"
  >
    <h2
      v-if="title"
      class="q-mb-md block text-center"
      v-html="title"
    />
    <div
      class="q-px-md text-center"
      v-html="message"
    />
    <InputText
      v-if="type === DialogType.PROMPT"
      v-model="promptInput.value"
      v-bind="promptInput.props"
      class="q-mt-md"
      new-design
    />
    <template #buttons>
      <BaseButton
        v-if="!hideCancel && type !== DialogType.ALERT"
        :label="cancelLabel"
        unelevated
        v-bind="cancelButtonBind"
        @click="cancel"
      />
      <BaseButton
        v-bind="confirmButtonBind"
        :label="confirmButtonLabel"
        @click="confirm"
      />
    </template>
  </Modal>
</template>

<script setup lang="ts">
import type { HTMLAttributes, VNodeProps } from "vue";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, InputText } from "shared/components/base";
import type { Props as BaseButtonProps } from "shared/components/base/BaseButton.vue";
import type { InputTextProps } from "shared/components/base/InputText.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import { DialogTheme, DialogType } from "shared/modals/types";

type ModalButtonProps = BaseButtonProps & HTMLAttributes & VNodeProps;

export interface Props {
  type?: DialogType;
  theme?: DialogTheme;
  title?: string;
  hideCancel?: boolean;
  confirmLabel?: string;
  confirmButtonProps?: ModalButtonProps;
  cancelLabel?: string;
  cancelButtonProps?: ModalButtonProps;
  message?: string;
  dialogStyle?: object;
  promptInput?: { props: InputTextProps; value: string };
  persistent?: boolean;
  offsetMenu?: boolean;
}

type Events = {
  close: [];
  cancel: [];
  confirm: [string | boolean];
};

const props = withDefaults(defineProps<Props>(), {
  type: DialogType.CONFIRM,
  theme: DialogTheme.DEFAULT,
  title: "",
  confirmLabel: "",
  confirmButtonProps: () => ({}),
  cancelLabel: () => getLocaleText("global.cancel"),
  cancelButtonProps: () => ({}),
  message: "",
  dialogStyle: () => ({}),
  promptInput: () => ({ props: {}, value: "" }),
});

const emit = defineEmits<Events>();

const confirmColor = computed(() => {
  if (props.theme === DialogTheme.SOCIAL) return "primary";
  if (props.theme === DialogTheme.DANGER) return "danger";

  return "primary";
});

const cancelButtonBind = computed(() => ({
  newDesign: true,
  color: "secondary",
  class: "flex-1",
  ...props.cancelButtonProps,
}));

const confirmButtonLabel = computed(() => {
  if (props.confirmLabel) return props.confirmLabel;
  if (props.type === DialogType.ALERT) return getLocaleText("global.ok");

  return getLocaleText("global.confirm");
});

const confirmButtonBind = computed(() => ({
  newDesign: true,
  color: confirmColor.value,
  class: "flex-1",
  ...props.confirmButtonProps,
}));

function cancel() {
  emit("cancel");
  emit("close");
}

function confirm() {
  emit(
    "confirm",
    props.type === DialogType.PROMPT ? props.promptInput.value : true
  );

  emit("close");
}
</script>

<style lang="scss" scoped>
:deep(.streem-modal2) {
  width: auto !important;
  margin: 0 auto !important;
}

:deep(.streem-modal2-buttons) {
  border-top: none !important;
  display: flex;
  padding: var(--s-spacing-lg);
  padding-top: 0;
  gap: var(--s-spacing-lg);
  justify-content: center;
}
</style>
